import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  listSolicitacoes,
  getSolicitacoesCounts,
  updateSolicitacaoStatus,
  deleteSolicitacao,
  STATUS_SOLICITACAO,
  STATUS_SOLICITACAO_LABEL,
} from "../services/solicitacoesService";
import StatCard from "../components/ui/StatCard";
import Loading, { ErrorBox } from "../components/ui/Loading";
import ConfirmDialog from "../components/ui/ConfirmDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function SolicitacoesPage() {
  const { C } = useTheme();
  const [lista, setLista] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [detalhe, setDetalhe] = useState(null); // solicitação sendo visualizada
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const [sols, cts] = await Promise.all([
        listSolicitacoes(),
        getSolicitacoesCounts().catch(() => null),
      ]);
      setLista(sols);
      setCounts(cts);
      setError(null);
    } catch {
      setError("Não foi possível carregar as solicitações.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  const filtrados = useMemo(() => {
    if (filtroStatus === "todos") return lista;
    const v = Number(filtroStatus);
    return lista.filter((s) => s.status === v);
  }, [lista, filtroStatus]);

  async function changeStatus(id, novoStatus) {
    try {
      await updateSolicitacaoStatus(id, Number(novoStatus));
      await reload();
    } catch {
      alert("Erro ao atualizar status.");
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteSolicitacao(confirmDelete.id);
      setConfirmDelete(null);
      await reload();
    } catch {
      alert("Erro ao remover.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
          📩 Solicitações
        </h1>
        <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
          Pedidos de reserva recebidos pelo site público
        </p>
      </div>

      {counts && (
        <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
          <StatCard icon="🆕" label="Novas" value={counts.nova} sub="A responder" color={C.red} />
          <StatCard icon="💬" label="Em contato" value={counts.emContato} sub="Em negociação" color={C.accent} />
          <StatCard icon="✅" label="Convertidas" value={counts.convertidasUltimoMes} sub="Último mês" color={C.green} />
          <StatCard icon="🗑" label="Total" value={counts.total} sub="Histórico" color={C.textMuted} />
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { val: "todos", label: `Todos (${lista.length})` },
          { val: "0", label: "Novas" },
          { val: "1", label: "Em contato" },
          { val: "2", label: "Convertidas" },
          { val: "3", label: "Descartadas" },
        ].map((t) => (
          <button
            key={t.val}
            onClick={() => setFiltroStatus(t.val)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: filtroStatus === t.val ? `1px solid ${C.accent}` : `1px solid ${C.border}`,
              background: filtroStatus === t.val ? C.accentDim : "transparent",
              color: filtroStatus === t.val ? C.accent : C.textMuted,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div
          style={{
            background: C.bgCard,
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "140px 1.5fr 1.5fr 100px 130px 100px",
              padding: "12px 20px",
              borderBottom: `1px solid ${C.border}`,
              gap: 8,
            }}
          >
            {["Data", "Nome", "Contato", "Desejada", "Status", "Ações"].map((h, i) => (
              <span
                key={i}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                {h}
              </span>
            ))}
          </div>
          {filtrados.map((s) => (
            <div
              key={s.id}
              style={{
                display: "grid",
                gridTemplateColumns: "140px 1.5fr 1.5fr 100px 130px 100px",
                padding: "14px 20px",
                borderBottom: `1px solid ${C.border}`,
                alignItems: "center",
                gap: 8,
                background: s.status === 0 ? C.redDim : "transparent",
              }}
            >
              <span style={{ fontSize: 12, color: C.textMuted }}>{fmtDateTime(s.criadoEm)}</span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.nome}
                </div>
                {s.numeroPessoas && (
                  <div style={{ fontSize: 11, color: C.textMuted }}>
                    {s.numeroPessoas} pessoa{s.numeroPessoas === 1 ? "" : "s"}
                  </div>
                )}
              </div>
              <div style={{ minWidth: 0, fontSize: 11, color: C.textMuted }}>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.email}
                </div>
                {s.telefone && (
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.telefone}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 12, color: C.text }}>{fmtDate(s.dataDesejada)}</span>
              <select
                value={s.status}
                onChange={(e) => changeStatus(s.id, e.target.value)}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  background: C.inputBg,
                  color: C.text,
                  fontSize: 11,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {Object.entries(STATUS_SOLICITACAO_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => setDetalhe(s)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: `1px solid ${C.border}`,
                    background: "transparent",
                    color: C.textMuted,
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                  title="Ver detalhes"
                >
                  👁
                </button>
                <button
                  onClick={() => setConfirmDelete(s)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: `1px solid ${C.red}60`,
                    background: "transparent",
                    color: C.red,
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                  title="Remover"
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
          {filtrados.length === 0 && (
            <div style={{ padding: 24, fontSize: 13, color: C.textMuted, textAlign: "center" }}>
              Nenhuma solicitação nesta aba.
            </div>
          )}
        </div>
      )}

      {/* Modal de detalhe */}
      {detalhe && (
        <div
          onClick={() => setDetalhe(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.bgCard,
              borderRadius: 14,
              border: `1px solid ${C.border}`,
              padding: 24,
              width: "100%",
              maxWidth: 560,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 12 }}>
              Solicitação · {detalhe.nome}
            </h2>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 14 }}>
              Recebida em {fmtDateTime(detalhe.criadoEm)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, fontSize: 13, color: C.text }}>
              <div style={{ color: C.textMuted }}>Email:</div>
              <div>{detalhe.email}</div>
              <div style={{ color: C.textMuted }}>Telefone:</div>
              <div>{detalhe.telefone || "—"}</div>
              <div style={{ color: C.textMuted }}>Data desejada:</div>
              <div>{fmtDate(detalhe.dataDesejada)}</div>
              <div style={{ color: C.textMuted }}>Pessoas:</div>
              <div>{detalhe.numeroPessoas ?? "—"}</div>
              <div style={{ color: C.textMuted }}>Origem:</div>
              <div>{detalhe.origem}</div>
              <div style={{ color: C.textMuted }}>IP:</div>
              <div style={{ fontFamily: "monospace", fontSize: 11 }}>{detalhe.ipOrigem || "—"}</div>
            </div>
            {detalhe.mensagem && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Mensagem
                </div>
                <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {detalhe.mensagem}
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
              <button
                onClick={() => setDetalhe(null)}
                style={{
                  padding: "10px 20px",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: "transparent",
                  color: C.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remover solicitação?"
        message={`Remover solicitação de ${confirmDelete?.nome ?? ""}? Essa ação não pode ser desfeita.`}
        confirmLabel="Remover"
        danger
        loading={deleting}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </div>
  );
}
